import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-medium shadow-none transition-[color,background-color,border-color,box-shadow,transform] duration-200 outline-none [&_svg]:relative [&_svg]:z-[1] [&_svg]:box-content [&_svg]:transition-colors [&_svg]:duration-200 disabled:pointer-events-none disabled:opacity-50 cursor-pointer focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 active:translate-y-0 active:scale-[0.985]",
  {
    variants: {
      variant: {
        default:
          "bg-secondary text-secondary-foreground hover:bg-secondary/85 hover:shadow-[0_10px_18px_-14px_rgb(15_23_42/0.22)]",
        primary:
          "bg-primary text-primary-foreground shadow-[0_10px_22px_-16px_oklch(var(--primary)/0.52)] hover:bg-primary/92 hover:text-primary-foreground hover:shadow-[0_14px_28px_-18px_oklch(var(--primary)/0.44)]",
        secondary:
          "bg-muted text-foreground hover:bg-muted/84 hover:shadow-[0_10px_18px_-14px_rgb(15_23_42/0.18)]",
        ghost:
          "bg-transparent text-foreground shadow-none hover:bg-accent hover:text-accent-foreground",
        outline:
          "border border-border/80 bg-background text-foreground hover:border-primary/28 hover:bg-accent/60 hover:text-foreground",
        link: "overflow-visible bg-transparent p-0 text-primary shadow-none hover:text-primary/80 hover:underline underline-offset-4",
        destructive:
          "bg-destructive text-white shadow-[0_10px_22px_-16px_rgb(220_38_38/0.45)] hover:bg-destructive/92 hover:text-white hover:shadow-[0_14px_28px_-18px_rgb(220_38_38/0.35)] focus-visible:ring-destructive/30",
      },
      size: {
        default: "h-9 px-3.5 py-2 text-[13px]",
        sm: "h-8 px-3 text-xs",
        lg: "h-10 px-5",
        icon: "h-9 w-9 p-0",
        "icon-xs": "h-7 w-7 p-0",
        "icon-sm": "h-8 w-8 p-0",
        "icon-lg": "h-10 w-10 p-0",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
      fullWidth: false,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, fullWidth, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, fullWidth }), className)}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
